// ─────────────────────────────────────────────────────────────────────────────
// RUNNING AN ACTION AND SAYING WHAT HAPPENED
//
// Testing found buttons all over Core that "do nothing". Almost none of them
// were missing a handler — five were, and those were a parser misreading. What
// they had was a handler that calls the database and never looks at the answer.
//
// When the call succeeds, that is fine and the screen reloads. When it fails —
// permission refused, a control that says no, or PostgREST unable to see a
// function it has not cached — nothing happens and nothing is said. The button
// is indistinguishable from one that was never wired.
//
// Twenty-three of them. Every "dead button" report that was not a routing
// problem looks like this.
//
// WHY A HELPER RATHER THAN FIXING TWENTY-THREE HANDLERS: because the
// twenty-fourth will be written the same way. A helper is the thing a person
// reaches for, and the failure handling comes with it.
//
// It deliberately does NOT swallow anything. A refusal from the database is
// usually the most useful sentence on the screen — "this period is closed",
// "the approver cannot also release" — and those are written to be read by the
// person who hit them.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Run an action, say what happened, and reload on success.
 *
 *   await runAction(() => OPS.taskAdd(...), {
 *     say: setMsg,              // where to put the message
 *     onDone: load,             // refresh after it works
 *     success: "Task added.",
 *   });
 */
export async function runAction(fn, opts = {}) {
  const { say, onDone, success, busy } = opts;
  if (busy) busy(true);
  if (say) say("");
  try {
    const r = await fn();

    // A wrapper that returns { ok, error } — most of ours do.
    if (r && typeof r === "object" && "ok" in r && !r.ok) {
      if (say) say(messageFor(r.error));
      return { ok: false, error: r.error };
    }
    // A raw supabase-js response.
    if (r && typeof r === "object" && r.error) {
      if (say) say(messageFor(r.error));
      return { ok: false, error: r.error };
    }
    // Not signed in is a different thing from a failure, and saying so saves
    // somebody looking for a fault that is not there.
    if (r && typeof r === "object" && r.live === false) {
      if (say) say("Not signed in — this could not be saved.");
      return { ok: false, error: "not signed in" };
    }

    if (say && success) say(success);
    if (onDone) await onDone();
    return { ok: true, data: r && r.data };
  } catch (e) {
    if (say) say(messageFor(e));
    return { ok: false, error: e };
  } finally {
    if (busy) busy(false);
  }
}

/**
 * Turn whatever came back into a sentence worth reading.
 *
 * The database's own refusals are written for the person who hit them, so they
 * are passed through unchanged. The ones worth translating are the technical
 * failures that name a cause nobody can act on.
 */
export function messageFor(err) {
  const raw =
    (err && (err.message || err.msg || err.hint || err.details)) ||
    (typeof err === "string" ? err : "") ||
    "That did not work, and nothing said why.";

  // PostgREST caches the schema at startup. A function created afterwards is
  // invisible to it and reports as missing — which reads as a broken feature
  // rather than a stale cache, and cost a morning of testing.
  if (/schema cache|Could not find the function/i.test(raw)) {
    return "The API cannot see that function yet — its schema cache is stale " +
           "after a recent change. It needs reloading; ask whoever deploys Core.";
  }
  if (/permission denied/i.test(raw)) {
    return "You do not have permission for that. If you should have, your Core " +
           "role may not match your sign-in — check with System admin.";
  }
  if (/JWSError|JWT|token/i.test(raw)) {
    return "Your session has expired. Sign in again.";
  }
  if (/Failed to fetch|NetworkError|ERR_/i.test(raw)) {
    return "Could not reach the database. Check the connection and try again.";
  }
  if (/violates not-null|null value in column/i.test(raw)) {
    const col = /column "([^"]+)"/.exec(raw);
    return col ? `"${col[1]}" is required and was empty.` : "A required field was empty.";
  }
  if (/duplicate key|already exists/i.test(raw)) {
    return "That already exists.";
  }
  // Everything else is the database talking, and it is usually the most useful
  // sentence available.
  return raw;
}
